import { useState } from "react";
import { MdEdit } from "react-icons/md";

import Button from "../../../components/Button";
import { useToaster } from "../../../components/toaster";
import { RokaApiError } from "../../../errors/RokaApiError";
import { useAdminApi } from "../../../hooks/api-hooks/useAdminApi";

interface SensorReferenceFieldProps {
  siteName: string;
  sensorName: string;
  currentRaw: number | undefined;
  reference: number | undefined;
  onUpdated: (newReference: number, newCurrent: number) => void;
}

const SensorReferenceField = ({
  siteName,
  sensorName,
  currentRaw,
  reference,
  onUpdated,
}: SensorReferenceFieldProps) => {
  const { updateSensorReference } = useAdminApi();
  const { showToaster } = useToaster();
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = () => {
    setInputValue(reference !== undefined ? String(reference) : "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    const value = parseFloat(inputValue);
    if (isNaN(value)) {
      showToaster("Ugyldig verdi", { type: "error" });
      return;
    }
    setIsSaving(true);
    try {
      const result = await updateSensorReference(siteName, sensorName, value);
      onUpdated(result.currentRawReference, result.current);
      showToaster(`Referanse for "${sensorName}" ble oppdatert!`, {
        type: "success",
      });
      setIsEditing(false);
    } catch (error) {
      if (error instanceof RokaApiError) {
        showToaster(`Kunne ikke oppdatere referanse: ${error.message}`, {
          type: "error",
          errorId: error.requestId,
        });
      } else {
        showToaster("En uventet feil oppstod", { type: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleZeroOut = async () => {
    setIsSaving(true);
    try {
      const result = await updateSensorReference(siteName, sensorName);
      onUpdated(result.currentRawReference, result.current);
      showToaster(`Referanse for "${sensorName}" ble nullstilt!`, {
        type: "success",
      });
      setIsEditing(false);
    } catch (error) {
      if (error instanceof RokaApiError) {
        showToaster(`Kunne ikke nullstille referanse: ${error.message}`, {
          type: "error",
          errorId: error.requestId,
        });
      } else {
        showToaster("En uventet feil oppstod", { type: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <>
        <span className="sensor-raw">Rå: {currentRaw ?? "—"}</span>
        <span className="reference-edit">
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={isSaving}
          />
          <Button
            className="save-button"
            onClick={handleSave}
            disabled={isSaving}
          >
            Lagre
          </Button>
          <Button
            className="zero-button"
            buttonStyle="outline"
            onClick={handleZeroOut}
            disabled={isSaving}
          >
            Null ut nå
          </Button>
          <Button
            className="cancel-button"
            buttonStyle="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            ✗
          </Button>
        </span>
      </>
    );
  }

  return (
    <>
      <span className="sensor-raw">Rå: {currentRaw ?? "—"}</span>
      <span className="sensor-reference">
        Referanse: {reference ?? "—"}
        <button
          className="edit-reference-btn"
          onClick={handleEdit}
          title="Rediger referanse"
        >
          <MdEdit />
        </button>
      </span>
    </>
  );
};

export default SensorReferenceField;
